import {
  // Existing leaderboards
  useGetHomeRunLeadersQuery,
  useGetBattingAverageLeadersQuery,
  useGetRbiLeadersQuery,
  useGetHitsLeadersQuery,
  // New hitting stats
  useGetOnBasePercentageLeadersQuery,
  useGetSluggingAverageLeadersQuery,
  useGetRunsLeadersQuery,
  useGetDoublesLeadersQuery,
  useGetTriplesLeadersQuery,
  useGetWalksLeadersQuery,
  useGetStrikeoutsLeadersQuery,
  // Pitching stats
  useGetWinsLeadersQuery,
  useGetEraLeadersQuery,
  useGetPitchingStrikeoutsLeadersQuery,
  useGetPitchingWalksLeadersQuery,
  useGetHitsAllowedLeadersQuery,
  useGetEarnedRunsLeadersQuery,
  useGetCompleteGamesLeadersQuery,
  useGetShutoutsLeadersQuery,
  useGetSavesLeadersQuery,
  useGetInningsPitchedLeadersQuery,
  useGetHomeRunsAllowedLeadersQuery,
  // Fielding stats
  useGetFieldingGamesLeadersQuery,
  useGetChancesLeadersQuery,
  useGetPutoutsLeadersQuery,
  useGetAssistsLeadersQuery,
  useGetErrorsLeadersQuery,
  useGetFieldingPercentageLeadersQuery,
  useGetRangeFactorLeadersQuery,
} from "../store/api/mlbApi";
import {
  HITTING_STATS,
  PITCHING_STATS,
  FIELDING_STATS,
  LEADERBOARD_CATEGORIES,
} from "../constants/stats";

/**
 * Custom hook to get leaderboard data for a specific stat
 * @param {string} statName - The name of the stat (e.g., 'homeruns', 'wins', 'putouts')
 * @param {Object} options - Query options (page, pageSize, etc.)
 * @returns {Object} - RTK Query result object
 */
export const useLeaderboardData = (statName, options = {}) => {
  const { page = 1, pageSize = 25, ...otherOptions } = options;

  // Map stat names to their corresponding RTK Query hooks
  const statToHookMap = {
    // Hitting stats
    [HITTING_STATS.HOME_RUNS]: useGetHomeRunLeadersQuery,
    [HITTING_STATS.BATTING_AVERAGE]: useGetBattingAverageLeadersQuery,
    [HITTING_STATS.RBI]: useGetRbiLeadersQuery,
    [HITTING_STATS.HITS]: useGetHitsLeadersQuery,
    [HITTING_STATS.ON_BASE_PERCENTAGE]: useGetOnBasePercentageLeadersQuery,
    [HITTING_STATS.SLUGGING_AVERAGE]: useGetSluggingAverageLeadersQuery,
    [HITTING_STATS.RUNS]: useGetRunsLeadersQuery,
    [HITTING_STATS.DOUBLES]: useGetDoublesLeadersQuery,
    [HITTING_STATS.TRIPLES]: useGetTriplesLeadersQuery,
    [HITTING_STATS.WALKS]: useGetWalksLeadersQuery,
    [HITTING_STATS.STRIKEOUTS]: useGetStrikeoutsLeadersQuery,

    // Pitching stats
    [PITCHING_STATS.WINS]: useGetWinsLeadersQuery,
    [PITCHING_STATS.ERA]: useGetEraLeadersQuery,
    [PITCHING_STATS.STRIKEOUTS]: useGetPitchingStrikeoutsLeadersQuery,
    [PITCHING_STATS.WALKS]: useGetPitchingWalksLeadersQuery,
    [PITCHING_STATS.HITS_ALLOWED]: useGetHitsAllowedLeadersQuery,
    [PITCHING_STATS.EARNED_RUNS]: useGetEarnedRunsLeadersQuery,
    [PITCHING_STATS.COMPLETE_GAMES]: useGetCompleteGamesLeadersQuery,
    [PITCHING_STATS.SHUTOUTS]: useGetShutoutsLeadersQuery,
    [PITCHING_STATS.SAVES]: useGetSavesLeadersQuery,
    [PITCHING_STATS.INNINGS_PITCHED]: useGetInningsPitchedLeadersQuery,
    [PITCHING_STATS.HOME_RUNS_ALLOWED]: useGetHomeRunsAllowedLeadersQuery,

    // Fielding stats
    [FIELDING_STATS.GAMES]: useGetFieldingGamesLeadersQuery,
    [FIELDING_STATS.CHANCES]: useGetChancesLeadersQuery,
    [FIELDING_STATS.PUTOUTS]: useGetPutoutsLeadersQuery,
    [FIELDING_STATS.ASSISTS]: useGetAssistsLeadersQuery,
    [FIELDING_STATS.ERRORS]: useGetErrorsLeadersQuery,
    [FIELDING_STATS.FIELDING_PERCENTAGE]: useGetFieldingPercentageLeadersQuery,
    [FIELDING_STATS.RANGE_FACTOR]: useGetRangeFactorLeadersQuery,
  };

  const queryHook = statToHookMap[statName];

  if (!queryHook) {
    console.warn(`No leaderboard hook found for stat: ${statName}`);
    return {
      data: null,
      isLoading: false,
      error: { message: `Unknown stat: ${statName}` },
    };
  }

  // Call the appropriate hook with the options
  return queryHook({ page, pageSize, ...otherOptions });
};

/**
 * Get the display name for a stat
 * @param {string} statName - The stat name
 * @returns {string} - Display name
 */
export const getStatDisplayName = (statName) => {
  const displayNames = {
    // Existing stats
    homeruns: "Home Runs",
    battingAverage: "Batting Average",
    rbis: "RBIs",
    hits: "Hits",

    // New hitting stats
    onBasePercentage: "On-Base Percentage",
    sluggingAverage: "Slugging Average",
    runs: "Runs",
    doubles: "Doubles",
    triples: "Triples",
    walks: "Walks",
    strikeouts: "Strikeouts",

    // Pitching stats
    wins: "Wins",
    era: "ERA",
    strikeOuts: "Strikeouts",
    baseOnBalls: "Walks",
    hitsAllowed: "Hits Allowed",
    earnedRuns: "Earned Runs",
    completeGames: "Complete Games",
    shutouts: "Shutouts",
    saves: "Saves",
    inningsPitched: "Innings Pitched",
    homeRunsAllowed: "Home Runs Allowed",

    // Fielding stats
    g: "Games",
    ch: "Chances",
    po: "Putouts",
    a: "Assists",
    e: "Errors",
    "FLD%": "Fielding %",
    rf: "Range Factor",
  };

  return displayNames[statName] || statName;
};

/**
 * Get the stat categories and their available stats
 * @returns {Object} - Object with category names as keys and stat arrays as values
 */
export const getStatCategories = () => {
  return LEADERBOARD_CATEGORIES;
};
